/**
 * Created by piotr.pozniak@thebeaverhead.com on 23/10/2018.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import classNames from "classnames";
import PageTemplate from "./../templates/PageTemplate";
import WidgetPreview from "../component/WidgetSettings/WidgetPreview";
import SetupTab from "./CalendarPage/SetupTab";
import DesignTab from "../component/WidgetSettings/DesignTab";
import InstallTab from "../component/WidgetSettings/InstallTab";
import LoadingIndicator from "../../calendar/components/LoadingIndicator";
import IntegrationContext, {
  getStrategy,
} from "../../calendar/strategies/IntegrationContext";
import { store } from "../store";
import { useCalendarStore } from "../../../hooks/redux/calendar";
import {
  calendarSettingsValidator,
  hasRemainingWidgetQuota,
  updateJquery,
} from "../tools";
import Calendar from "../../calendar/components/Calendar";
import { useBuilderStore } from "../../../hooks/redux/builder";
import BuilderIdentityBand from "../component/WidgetSettings/BuilderIdentityBand/BuilderIdentityBand";
import BuilderSettingsSheet from "../component/WidgetSettings/BuilderSettingsSheet/BuilderSettingsSheet";
import useWindowSize from "../../../hooks/useWindowSize";
import { useUserStore } from "../../../hooks/redux/user";
import { useCalendarsStore } from "../../../hooks/redux/calendars";
import { useAppStore } from "../../../hooks/redux/app";
import { useEventsStore } from "../../../hooks/redux/events";
import { useDomainsStore } from "../../../hooks/redux/domains";
import { useIntegrationsStore } from "../../../hooks/redux/integrations";
import NoEventsAlert from "./CalendarPage/NoEventsAlert";
import IntegrationIssueNotification, {
  hasIntegrationErrors,
} from "../component/IntegrationIssueNotification";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import useSetupChangesTrackerHook from "../hooks/useSetupChangesTrackerHook";
import useQuery from "../../../hooks/useQuery";
import { getWidgetItemSlugFromUrl } from "../../../helpers/linking";
import { getPublicWidgetUrl } from "../../../helpers/url";
import styles from "./CalendarPage.module.scss";

window.__DCE = window.__DCE || {};
window.__DCE.calendar = window.__DCE.calendar || {};

const PageTabs = [
  {
    label: "Setup",
    id: "setup_tab",
    additionalClassName: "active show",
  },
  {
    label: "Design",
    id: "design_tab",
    additionalClassName: "",
  },
  {
    label: "Install",
    id: "install_tab",
    additionalClassName: "",
  },
];

const CalendarPage = (props) => {
  const uuid = props.match.params.uuid;

  const { app, toggleWidgetPreview } = useAppStore();
  const { user } = useUserStore();
  const { calendar } = useCalendarStore();
  const { calendars } = useCalendarsStore();
  const { clearErrors } = useAppStore();
  const { events } = useEventsStore();
  const { domains } = useDomainsStore();
  const { integrations } = useIntegrationsStore();
  const { currentOrganization } = useCurrentOrganizationStore();
  const query = useQuery();

  const { deleteCalendar, fetchCalendar, setCalendar, updateCalendar } =
    useCalendarStore();

  const { overwriteDesignProp, overwriteFilterProp } = useBuilderStore();

  const history = useHistory();
  const location = useLocation();
  const [state, setState] = useState({
    calendarModel: null,
    errors: {},
  });
  const [panelHidden, setPanelHidden] = useState(false);
  const settingsSheetOpen = location.hash === "#settings";
  const { width } = useWindowSize();
  const isMobile = width !== undefined && width < 768;

  const onTogglePanel = () => setPanelHidden((hidden) => !hidden);
  const onToggleSettingsSheet = () => {
    if (settingsSheetOpen) {
      history.goBack();
    } else {
      history.push({ ...location, hash: "#settings" });
    }
  };
  const onCloseSettingsSheet = () => history.goBack();
  const onPreview = () => toggleWidgetPreview(true);

  const singleItemSlug = useMemo(() => {
    return getWidgetItemSlugFromUrl("event");
  }, [query]);

  const { trackChanges, setOriginValues, hasChanges, updateOriginField } =
    useSetupChangesTrackerHook();
  const initialWidgetSettingsSynced = useRef(false);

  const [savePointEventsSettings, setSavePointEventsSettings] = useState(null);

  const isWidgetInitialized = useMemo(() => {
    return calendar.model &&
      calendar.model.slug &&
      integrations.fetchSuccess &&
      calendar.model.integration
      ? true
      : false;
  }, [integrations.fetchSuccess, calendar.model]);

  const integration = isWidgetInitialized
    ? integrations.collection.find(
        (integration) => integration.uuid === calendar.model.integration.uuid
      )
    : null;

  const strategy = useMemo(
    () =>
      getStrategy(
        isWidgetInitialized ? calendar.model.integration.type : null,
        calendar.model
      ),
    [isWidgetInitialized, calendar.model]
  );

  useEffect(() => {
    (async () => {
      await fetchCalendar(props.match.params.uuid, true);
    })();

    return () => {
      setCalendar(null);
    };
  }, []);

  useEffect(() => {
    if (calendar.model && calendar.model.integration) {
      setState({
        ...state,
        calendarModel: {
          ...calendar.model,
          integration_uuid: calendar.model.integration.uuid,
        },
      });
      setOriginValues({ ...calendar.model });
      // Re-arm the widget_settings sync guard below: this snapshot may
      // predate the widget preview's own default-materializing postMessage,
      // so the next widgetSettingsUpdateTimestamp firing must be treated as
      // a baseline sync again, not a change comparison.
      initialWidgetSettingsSynced.current = false;
    }
  }, [calendar.model]);

  useEffect(() => {
    if (calendar.updateError) {
      setState({
        ...state,
        calendarModel: {
          ...calendar.model,
          integration_uuid: calendar.model.integration.uuid,
        },
      });
      setOriginValues({ ...calendar.model });
      initialWidgetSettingsSynced.current = false;

      setTimeout(() => clearErrors(), 5000);
    }
  }, [calendar.updateError]);

  useEffect(() => {
    if (calendar.fetchSuccess) {
      if (calendar.model && !calendar.model.integration) {
        //history.push("/dashboard");
      }
    }

    if (calendars.fetchSuccess) {
      setTimeout(updateJquery, 200);
    }
  }, [calendar.fetchSuccess, events.fetchSuccess]);

  useEffect(() => {
    if (calendar.updateSuccess) {
      setSavePointEventsSettings(
        Object.assign({}, calendar.model.widget_settings)
      );

      setTimeout(clearErrors, 4500);
      setOriginValues(calendar.model);
      initialWidgetSettingsSynced.current = false;
    }
  }, [calendar.updateSuccess]);

  /**
   * track changes if design settings were updated
   */
  useEffect(() => {
    if (isWidgetInitialized && app.widgetSettingsUpdateTimestamp) {
      // See AlertPage.js for the full explanation: the widget preview's
      // first postMessage after init materializes builder-default keys not
      // present in the origin snapshot — absorb that into the baseline
      // instead of tracking it as a change.
      if (!initialWidgetSettingsSynced.current) {
        initialWidgetSettingsSynced.current = true;
        updateOriginField("widget_settings", calendar.model.widget_settings);
        return;
      }
      trackChanges("widget_settings", calendar.model.widget_settings);
    }
  }, [app.widgetSettingsUpdateTimestamp, isWidgetInitialized]);

  useEffect(() => {
    if (isWidgetInitialized) {
      setSavePointEventsSettings(
        Object.assign({}, calendar.model.widget_settings)
      );
    }
  }, [isWidgetInitialized]);

  /**
   *
   * @param fieldName
   * @param value
   */
  const onUpdateField = (fieldName, value) => {
    const updatedModel = {
      ...state.calendarModel,
    };
    trackChanges(fieldName, value);
    updatedModel[fieldName] = value;

    setState({
      calendarModel: updatedModel,
      errors: calendarSettingsValidator(updatedModel),
    });
  };

  /**
   * @param {string} fieldName
   * @param {string|array|object} value
   * @type {(function(*, *))|*}
   */
  const onUpdateFilterSettings = useCallback(
    (field, value) => {
      const updatedModel = {
        ...state.calendarModel,
      };
      updatedModel.filter_settings = {
        ...(updatedModel.filter_settings || {}),
      };

      updatedModel.filter_settings[field] = value;
      trackChanges("filter_settings", updatedModel.filter_settings);

      setState({
        calendarModel: updatedModel,
        errors: calendarSettingsValidator(updatedModel),
      });
    },
    [state.calendarModel]
  );

  /**
   * @param {string} fieldName
   * @param {string|array|object} value
   * @type {(function(*, *))|*}
   */
  const onUpdateWidgetSettings = useCallback(
    (field, value) => {
      const updatedModel = {
        ...state.calendarModel,
      };
      updatedModel.widget_settings = {
        ...(updatedModel.widget_settings || {}),
      };

      trackChanges(field, value);

      updatedModel.widget_settings[field] = value;

      setState({
        calendarModel: updatedModel,
        errors: calendarSettingsValidator(updatedModel),
      });
    },
    [state.calendarModel]
  );

  /**
   *
   * @param fieldName
   * @param value
   */
  const onUpdateStatus = useCallback(
    (value) => {
      const updatedModel = {
        ...state.calendarModel,
      };

      updatedModel.available = value;

      const isConfigValid = validateSettings(updatedModel);
      const hasValidSettings = calendarSettingsValidator(updatedModel);

      // console.log("onUpdateStatus", isConfigValid);
      setState({ ...state, errors: hasValidSettings });

      if (!isConfigValid || Object.keys(hasValidSettings).length) {
        console.log("Invalid settings, not updating calendar.");
        return false;
      }

      setState({
        ...state,
        calendarModel: updatedModel,
      });

      _onSettingsSubmit(updatedModel);
    },
    [state.calendarModel]
  );

  /**
   *
   */
  const onSettingsSubmit = async () => {
    const data = { ...state.calendarModel };
    const hasValidSettings = calendarSettingsValidator(data);

    setState({ ...state, errors: hasValidSettings });

    if (Object.keys(hasValidSettings).length) {
      return false;
    }
    _onSettingsSubmit(data);
  };

  /**
   *
   */
  const _onSettingsSubmit = async (data) => {
    if (calendar.update) {
      return false;
    }
    // check if integration has changed, if so, remove calendar filters;
    if (calendar.model.integration.uuid !== data.integration_uuid) {
      data.filter_settings = null;
    }

    // it overwrites the builder settings that are updated with redux but widget_settings contain other
    // settings that are not updated with redux
    const widgetSettings = {
      ...data.widget_settings,
    };

    data.widget_settings = {
      ...calendar.model.widget_settings,
    };

    // restore data.widget_settings using widgetSettings and omit 'design' and 'highlights' keys
    Object.keys(widgetSettings).forEach((key) => {
      if (key !== "design" && key !== "highlights") {
        data.widget_settings[key] = widgetSettings[key];
      }
    });

    delete data.builder;
    const isConfigValid = validateSettings(data);

    if (!isConfigValid) {
      return;
    }

    await updateCalendar(uuid, data);
  };

  /**
   *
   * @param data
   * @returns {boolean}
   */
  const validateSettings = (data) => {
    const canPublish = hasRemainingWidgetQuota(currentOrganization);

    if (data.available && !canPublish) {
      setState({
        ...state,
        errors: [
          "You have reached the limit of your plan. Please upgrade to publish more widgets.",
        ],
      });
      return false;
    }

    return true;
  };

  /**
   *
   */
  const onDeleteClick = async (e) => {
    e.preventDefault();

    if (calendar.fetch || calendar.delete) {
      return false;
    }

    await deleteCalendar(uuid);

    if (store.getState().calendar.deleteSuccess) {
      history.push("/calendars");
    }

    return false;
  };

  /**
   *
   */
  const onSettingsRestore = () => {
    if (
      window.confirm(
        "This will remove all the changes you have done since the last SAVE. Are you sure?"
      )
    ) {
      overwriteDesignProp(savePointEventsSettings.design);
    }
  };

  const loading =
    calendar.update ||
    calendar.fetch ||
    calendar.delete ||
    !calendar.model ||
    !calendar.model.template ||
    !isWidgetInitialized;

  const settingsTabsOptions = isWidgetInitialized ? PageTabs : [];

  const settingsTabs = settingsTabsOptions.map((item, idx) => {
    return (
      <li key={"settings-tab-" + idx} className="nav-item">
        <a
          className={"nav-link " + item.additionalClassName}
          href={"#" + item.id}
          data-toggle="tab"
        >
          {item.label}
        </a>
      </li>
    );
  });

  const designTab = isWidgetInitialized ? (
    <DesignTab
      disabled={loading}
      onSubmit={onSettingsSubmit}
      onRestore={onSettingsRestore}
      widget={calendar}
      settingsName={"design"}
      hasSetupChanged={hasChanges}
    />
  ) : null;

  const setupTab = isWidgetInitialized ? (
    <SetupTab
      integrations={integrations.collection}
      disabled={loading}
      onSubmit={onSettingsSubmit}
      isNew={false}
      overwriteDesignProp={overwriteDesignProp}
      overwriteFilterProp={overwriteFilterProp}
      user={user}
      calendarModel={state.calendarModel || {}}
      errors={state.errors}
      onUpdateField={onUpdateField}
      onUpdateFilterSettings={onUpdateFilterSettings}
      onUpdateWidgetSettings={onUpdateWidgetSettings}
      hasSetupChanged={hasChanges}
    />
  ) : null;

  const installTab = isWidgetInitialized ? (
    <InstallTab
      widget={calendar}
      disabled={loading}
      onSubmit={onSettingsSubmit}
      onUpdateStatus={onUpdateStatus}
      isNew={false}
      hasDomains={domains.collection.length ? true : false}
      widgetModel={state.calendarModel || {}}
      errors={state.errors}
      onUpdateField={onUpdateField}
      widgetTypeName={"Calendar"}
      shareableUrl={getPublicWidgetUrl(
        `c/${calendar.model.slug}`,
        calendar.model.widget_domain
      )}
      onUpdateWidgetSettings={onUpdateWidgetSettings}
    />
  ) : null;

  const calendarSettingsContent = (
    <>
      <div className={styles.header}>
        <ul className="nav nav-builder">{settingsTabs}</ul>
      </div>

      <div className={classNames("tab-content", styles.body)}>
        {setupTab}

        {designTab}

        {installTab}
      </div>
    </>
  );

  // Desktop only — RHS panel, toggled via the band's panel control. On
  // mobile the same tab content instead renders inside BuilderSettingsSheet
  // — never both at once, since SetupTab/DesignTab hold builder-store state
  // that isn't safe to have two live mounted instances of simultaneously.
  const calendarSidebar = !panelHidden && !isMobile ? (
    <div className={styles.sidebar} id="chat_sidebar">
      {calendarSettingsContent}
    </div>
  ) : null;

  const isUntitled = isWidgetInitialized && !calendar.model.name;
  const calendarName = !isWidgetInitialized
    ? "Loading..."
    : isUntitled
    ? "Untitled calendar"
    : calendar.model.name;

  const integrationIssuesInfo = isWidgetInitialized ? (
    <IntegrationIssueNotification
      integration_uuid={calendar.model.integration.uuid}
    />
  ) : null;

  /*  console.log(
    integrationIssuesInfo,
    isWidgetInitialized,
    events.fetchSuccess,
    events.collection.length
  );*/
  const noEventsToDisplayInfo =
    !hasIntegrationErrors(integration) &&
    isWidgetInitialized &&
    events.fetchSuccess &&
    !events.collection.length ? (
      <NoEventsAlert calendar={calendar} />
    ) : null;

  const calendarPreview = isWidgetInitialized ? (
    <React.Fragment>
      <WidgetPreview
        key={calendar.model.integration.type || new Date()}
        uuid={uuid}
        widget={calendar}
        widgetKind={"calendars"}
        className={styles.previewCard}
      >
        <Calendar
          calendarUUID={uuid}
          embedded={false}
          singleEventSlug={singleItemSlug}
        />
      </WidgetPreview>
      {integrationIssuesInfo}
      {noEventsToDisplayInfo}
    </React.Fragment>
  ) : (
    <LoadingIndicator />
  );

  return (
    <PageTemplate
      sidebarId="calendar"
      className={"page-wrapper-widget"}
      header={
        <BuilderIdentityBand
          name={calendarName}
          untitled={isUntitled}
          published={Boolean(calendar.model?.available)}
          indexHref="/calendars"
          indexLabel="Calendars"
          onPreview={onPreview}
          onDelete={onDeleteClick}
          panelHidden={panelHidden}
          onTogglePanel={onTogglePanel}
          settingsOpen={settingsSheetOpen}
          onToggleSettings={onToggleSettingsSheet}
        />
      }
    >
      <IntegrationContext.Provider value={strategy}>
        <div className={styles.builderRow}>
          <div className={styles.canvas}>{calendarPreview}</div>

          {calendarSidebar}
        </div>

        <div className="sidebar-overlay" data-reff="#chat_sidebar" />

        <BuilderSettingsSheet
          open={settingsSheetOpen && isMobile}
          onClose={onCloseSettingsSheet}
          title="Calendar settings"
        >
          {isMobile ? calendarSettingsContent : null}
        </BuilderSettingsSheet>
      </IntegrationContext.Provider>
    </PageTemplate>
  );
};

export default CalendarPage;
